import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { motion } from "framer-motion";
import { MessageSquare, Mail, Phone, MapPin, Send, ArrowRight, CheckCircle2, Globe, ShieldCheck, Zap, Users } from "lucide-react";
import { cn } from "@/lib/utils";

const CONTACT_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "P2 PRO Painel IPTV",
  url: "https://p2propaineliptv.com.br",
  logo: "https://p2propaineliptv.com.br/__l5e/assets-v1/7c25e136-c4b8-4ab8-b5c7-52ef6b9b87a3/p2pro-logo.png",
  areaServed: "BR",
  contactPoint: [{
    "@type": "ContactPoint",
    telephone: "+55-83-98614-7064",
    contactType: "customer support",
    areaServed: "BR",
    availableLanguage: ["Portuguese"],
    contactOption: "TollFree"
  }]
};

export const Route = createFileRoute("/contato")({
  head: () => ({
    meta: [
      { title: "Contato P2 PRO - Suporte e Vendas Especializado" },
      { name: "description", content: "Precisa de ajuda ou quer começar sua revenda IPTV? Fale com o time da P2 PRO. Atendimento rápido via WhatsApp." },
      { property: "og:title", content: "Contato P2 PRO - Suporte e Vendas Especializado" },
      { property: "og:description", content: "Fale com o time de especialistas da P2 PRO. Atendimento rápido via WhatsApp." },
    ],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(CONTACT_LD) }],
  }),
  component: ContactPage,
});

function ContactPage() {
  const whatsappUrl = `https://api.whatsapp.com/send?phone=5583986147064&text=${encodeURIComponent("Olá! Vim do site p2propaineliptv.com.br, gostaria de mais informações.")}`;

  return (
    <Layout>
      <main className="pt-32 pb-24 overflow-hidden bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6">
          <Breadcrumbs items={[{ label: "Contato", href: "/contato" }]} />

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-32 mt-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-electric/10 border border-electric/20 text-electric text-[11px] font-black uppercase tracking-[0.25em] mb-10">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-electric opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-electric"></span>
                </span>
                FALE COM A ELITE AGORA
              </div>
              
              <h1 className="text-6xl md:text-8xl font-black tracking-tighter leading-[0.9] mb-10 italic uppercase">
                CONTATO <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric to-blue-400">P2 PRO</span> ELITE
              </h1>
              
              <p className="text-xl text-gray-400 leading-relaxed mb-12 max-w-xl italic border-l-4 border-electric pl-6">
                Fale com a elite do IPTV. Nossa infraestrutura suporta milhares de conexões simultâneas com estabilidade real. Domine o mercado com quem entende.
              </p>

              <div className="flex flex-col sm:flex-row gap-6">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative px-12 py-7 bg-electric text-white rounded-2xl font-black text-lg uppercase tracking-wider flex items-center justify-center gap-4 overflow-hidden transition-all hover:shadow-[0_0_50px_rgba(0,210,255,0.5)] active:scale-95"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:animate-shimmer" />
                  <Phone className="w-6 h-6 animate-pulse" />
                  INICIAR ATENDIMENTO
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative"
            >
              {/* Floating Cards with Glassmorphism */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
                <div className="space-y-6 pt-16">
                  <div className="p-10 rounded-[3rem] bg-gradient-to-b from-white/[0.08] to-transparent border border-white/10 backdrop-blur-xl hover:border-electric/50 transition-all group hover:-translate-y-2 shadow-2xl">
                    <div className="w-16 h-16 rounded-2xl bg-electric/20 flex items-center justify-center mb-8 group-hover:bg-electric transition-colors">
                      <Zap className="w-8 h-8 text-electric group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3 italic uppercase">Recorde de Velocidade</h3>
                    <p className="text-gray-400 italic text-sm leading-relaxed">Atendimento técnico em média de 3 minutos. Sem filas, sem espera.</p>
                  </div>
                  
                  <div className="p-10 rounded-[3rem] bg-gradient-to-b from-white/[0.08] to-transparent border border-white/10 backdrop-blur-xl hover:border-electric/50 transition-all group hover:-translate-y-2 shadow-2xl">
                    <div className="w-16 h-16 rounded-2xl bg-electric/20 flex items-center justify-center mb-8 group-hover:bg-electric transition-colors">
                      <ShieldCheck className="w-8 h-8 text-electric group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3 italic uppercase">Garantia P2 PRO</h3>
                    <p className="text-gray-400 italic text-sm leading-relaxed">Segurança total nos seus créditos e suporte direto com os donos do servidor.</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="p-10 rounded-[3rem] bg-gradient-to-b from-white/[0.08] to-transparent border border-white/10 backdrop-blur-xl hover:border-electric/50 transition-all group hover:-translate-y-2 shadow-2xl">
                    <div className="w-16 h-16 rounded-2xl bg-electric/20 flex items-center justify-center mb-8 group-hover:bg-electric transition-colors">
                      <Globe className="w-8 h-8 text-electric group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3 italic uppercase">Escala Global</h3>
                    <p className="text-gray-400 italic text-sm leading-relaxed">Infraestrutura em datacenters internacionais para estabilidade máxima.</p>
                  </div>

                  <div className="p-10 rounded-[3rem] bg-gradient-to-b from-white/[0.08] to-transparent border border-white/10 backdrop-blur-xl hover:border-electric/50 transition-all group hover:-translate-y-2 shadow-2xl">
                    <div className="w-16 h-16 rounded-2xl bg-electric/20 flex items-center justify-center mb-8 group-hover:bg-electric transition-colors">
                      <Users className="w-8 h-8 text-electric group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3 italic uppercase">Comunidade VIP</h3>
                    <p className="text-gray-400 italic text-sm leading-relaxed">Acesso a grupos exclusivos de revendedores com dicas de vendas.</p>
                  </div>
                </div>
              </div>
              
              {/* Background abstract elements */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-electric/5 blur-[150px] -z-10 rounded-full animate-pulse" />
            </motion.div>
          </div>

          {/* New Call to Action Section */}
          <section className="relative mt-20 mb-32 overflow-hidden">
            <div className="absolute inset-0 bg-electric/10 rounded-[4rem] skew-y-1 transform scale-105 -z-10" />
            <div className="relative z-10 p-12 md:p-24 bg-[#111] border border-white/5 rounded-[4rem] shadow-3xl overflow-hidden group">
              <div className="absolute top-0 right-0 w-96 h-96 bg-electric/20 blur-[100px] -mr-48 -mt-48 rounded-full" />
              
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div>
                  <h2 className="text-5xl md:text-7xl font-black tracking-tighter mb-8 italic uppercase leading-none">
                    CHEGA DE <br />
                    <span className="text-electric">DESCULPAS</span>
                  </h2>
                  <p className="text-2xl text-gray-300 mb-12 italic font-light leading-relaxed">
                    Enquanto você pensa, outros estão lucrando. <br />
                    <span className="text-white font-bold">O próximo grande player pode ser você.</span>
                  </p>
                  
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 group/item">
                      <div className="w-10 h-10 rounded-full bg-electric/20 flex items-center justify-center group-hover/item:bg-electric transition-all">
                        <CheckCircle2 className="w-5 h-5 text-electric group-hover/item:text-white" />
                      </div>
                      <span className="text-lg font-bold italic uppercase tracking-widest group-hover/item:text-electric transition-colors">Início Imediato em 5 min</span>
                    </div>
                    <div className="flex items-center gap-4 group/item">
                      <div className="w-10 h-10 rounded-full bg-electric/20 flex items-center justify-center group-hover/item:bg-electric transition-all">
                        <CheckCircle2 className="w-5 h-5 text-electric group-hover/item:text-white" />
                      </div>
                      <span className="text-lg font-bold italic uppercase tracking-widest group-hover/item:text-electric transition-colors">Suporte Direto com Experts</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-center justify-center">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full"
                  >
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center justify-center w-full py-16 px-10 bg-white text-black rounded-[3rem] shadow-[0_30px_60px_rgba(0,0,0,0.5)] relative overflow-hidden group"
                    >
                      <div className="absolute inset-0 bg-electric opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      <div className="relative z-10 flex flex-col items-center">
                        <span className="text-sm tracking-[0.4em] font-black text-electric group-hover:text-white mb-6 uppercase">BOTÃO DE IMPACTO</span>
                        <h4 className="text-4xl md:text-5xl font-black italic uppercase text-center leading-none mb-8 group-hover:text-white">
                          QUERO DOMINAR O MERCADO AGORA
                        </h4>
                        <div className="w-20 h-20 bg-black group-hover:bg-white rounded-full flex items-center justify-center transition-all">
                          <ArrowRight className="w-10 h-10 text-white group-hover:text-electric rotate-[-45deg] group-hover:rotate-0 transition-transform duration-500" />
                        </div>
                      </div>
                    </a>
                  </motion.div>
                  
                  <p className="mt-8 text-sm text-gray-500 italic uppercase tracking-[0.3em] font-bold">
                    + de 2.500 revendedores ativos
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </Layout>
  );
}

